/**
 * Registers the email-template CLIENT tools with the AI tool bridge
 * (P2a tier-1 preset-fill, plan §P2a). Mounted GLOBALLY inside the
 * EventScopedAiAssistant subtree (EventLayoutShell) — unlike the page-scoped
 * AiGuestlistTools these tools need no page state, only the eventId from the
 * route, so they're available on every event page. Renders nothing.
 *
 * The create/update handlers ride the USER's own session (cookie + CSRF) and
 * can do exactly what the create-template page does — the proven FE renderer
 * builds the HTML; the model only supplies slot texts. Soft validation
 * failures are RETURNED as data so the model self-corrects.
 */
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAiToolBridge } from "@/lib/ai/ai-tool-bridge";
import {
  getEmailTemplate,
  getEmailTemplatesByEvent,
  updateEmailTemplate,
} from "@/lib/api/handlers/EmailTemplateHandling";
import { getDocumentTemplatesHandler } from "@/lib/api/handlers/DocumentTemplateHandling";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";
import { normalizeToFullLocale } from "@/lib/utils/locale-normalize";
import type { SlotFill } from "@/components/PCreateMailTemplate/data/presetSlots";
import type { TemplateCanvasSettings } from "@/components/PCreateMailTemplate/data/createTemplateCore";
import type { BlockDsl } from "@/components/PCreateMailTemplate/data/buildCraftJsFromBlocks";
import type { EmailTemplatePreset } from "@/components/PCreateMailTemplate/data/presets/_types";
import type { DocumentTemplatePreset } from "@/components/PCreateDocumentTemplate/data/presets/_types";
import { emailWebfontsAllowed } from "@/lib/fonts/builderFontCatalog";

/**
 * The preset corpora + renderer load LAZILY on first tool call: the mail and
 * document routes are code-split, and the preset modules pre-render every
 * preset at module-load time — importing them statically here would pull
 * that work (and the ~6k preset text literals) into the event shell chunk on
 * every page. A failed load resets the cache so the next call retries.
 */
type TemplateAiModules = {
  presets: typeof import("@/components/PCreateMailTemplate/data/emailTemplatePresets");
  slots: typeof import("@/components/PCreateMailTemplate/data/presetSlots");
  core: typeof import("@/components/PCreateMailTemplate/data/createTemplateCore");
  docPresets: typeof import("@/components/PCreateDocumentTemplate/data/documentTemplatePresets");
  docCore: typeof import("@/components/PCreateDocumentTemplate/data/createDocumentCore");
  compiler: typeof import("@/components/PCreateMailTemplate/data/buildCraftJsFromBlocks");
};

let modulesPromise: Promise<TemplateAiModules> | null = null;
function loadTemplateAiModules(): Promise<TemplateAiModules> {
  modulesPromise ??= Promise.all([
    import("@/components/PCreateMailTemplate/data/emailTemplatePresets"),
    import("@/components/PCreateMailTemplate/data/presetSlots"),
    import("@/components/PCreateMailTemplate/data/createTemplateCore"),
    import("@/components/PCreateDocumentTemplate/data/documentTemplatePresets"),
    import("@/components/PCreateDocumentTemplate/data/createDocumentCore"),
    import("@/components/PCreateMailTemplate/data/buildCraftJsFromBlocks"),
  ]).then(
    ([presets, slots, core, docPresets, docCore, compiler]) => ({
      presets,
      slots,
      core,
      docPresets,
      docCore,
      compiler,
    }),
    (err: unknown) => {
      modulesPromise = null;
      throw err;
    },
  );
  return modulesPromise;
}

// Intent = trailing segment of the preset id (mirrors TemplateGallery's
// INTENT_ORDER — a UI module we don't import here to keep this handler free
// of gallery rendering deps). Longest suffix first so `-save-the-date` wins.
const PRESET_INTENTS = [
  "save-the-date",
  "invitation",
  "acceptance",
  "decline",
  "reminder",
  "documents",
  "companion",
  "treatment",
  "ticket",
];

function intentOf(presetId: string): string | null {
  for (const intent of PRESET_INTENTS) {
    if (presetId.endsWith(`-${intent}`)) return intent;
  }
  return null;
}

/** Fillable presets: everything but the empty "blank" scaffold and the
 *  P9-T1 `featured` premium presets — those are curated for browse-and-
 *  direct-use (the gallery featured row / studio gallery), not for the AI
 *  to re-fill slot-by-slot (which would mangle their hand-built art
 *  direction). "Polish with AI" iterates an already-created candidate, so
 *  it does not need them in this create-from-corpus list. */
const fillablePresets = (m: TemplateAiModules): EmailTemplatePreset[] =>
  m.presets.EMAIL_TEMPLATE_PRESETS.filter((p) => p.id !== "blank" && !p.featured);

/** Fillable document presets (all printable tickets today; premium excluded
 *  for the same reason as {@link fillablePresets}). */
const fillableDocPresets = (m: TemplateAiModules): DocumentTemplatePreset[] =>
  m.docPresets.DOCUMENT_TEMPLATE_PRESETS.filter((p) => p.id !== "blank" && !p.featured);

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

type CreateInput = {
  presetId?: string;
  templateName?: string;
  languages?: string[];
  mailSubject?: Record<string, string>;
  fills?: Record<string, SlotFill>;
  colorOverrides?: { contentAreaBackgroundColor?: string; linkColor?: string };
  replyToMail?: string;
};

type CreateDocumentInput = {
  presetId?: string;
  name?: string;
  languages?: string[];
  fills?: Record<string, SlotFill>;
  colorOverrides?: { contentAreaBackgroundColor?: string; linkColor?: string };
};

type UpdateInput = {
  templateId?: string;
  fills?: Record<string, SlotFill>;
  mailSubject?: Record<string, string>;
};

type DslCanvas = {
  contentAreaWidth?: number;
  contentAreaHeight?: number;
  contentAreaBackgroundColor?: string;
  globalFont?: string;
  linkColor?: string;
};

type GenerateEmailInput = {
  templateName?: string;
  languages?: string[];
  canvas?: DslCanvas;
  subjects?: Record<string, string>;
  blocks?: BlockDsl["blocks"];
  replyToMail?: string;
};

type GenerateDocumentInput = {
  name?: string;
  languages?: string[];
  canvas?: DslCanvas;
  blocks?: BlockDsl["blocks"];
};

/** Normalizes a locale list; returns the offending entry on failure. */
function normalizeLocales(
  raw: string[],
): { ok: true; locales: string[] } | { ok: false; badLocale: string } {
  const locales: string[] = [];
  for (const entry of raw) {
    const full = normalizeToFullLocale(entry);
    if (!full) return { ok: false, badLocale: String(entry) };
    if (!locales.includes(full)) locales.push(full);
  }
  return { ok: true, locales };
}

/** Normalizes the keys of a per-locale map; values pass through untouched. */
function normalizeSubjects(
  raw: Record<string, string>,
): { ok: true; map: Record<string, string> } | { ok: false; badLocale: string } {
  const map: Record<string, string> = {};
  for (const [locale, value] of Object.entries(raw)) {
    const full = normalizeToFullLocale(locale);
    if (!full) return { ok: false, badLocale: locale };
    map[full] = String(value ?? "");
  }
  return { ok: true, map };
}

export function AiTemplateTools() {
  const { eventId = "" } = useParams<{ eventId: string }>();
  // Handlers registered ONCE (bridge identity must stay stable for the
  // unregister race-guard); they read the live eventId via a ref.
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;

  useEffect(() => {
    const { register, unregister } = useAiToolBridge.getState();

    const listTemplatePresets = async (rawInput: unknown) => {
      const m = await loadTemplateAiModules();
      const input = (rawInput ?? {}) as {
        type?: string;
        family?: string;
        intent?: string;
      };
      if (input.type === "document") {
        let presets = fillableDocPresets(m);
        if (input.family) {
          presets = presets.filter((p) => p.category === input.family);
        }
        return {
          ok: true,
          type: "document",
          presets: presets.map((p) => ({
            presetId: p.id,
            family: p.category,
            intent: "ticket",
            pageSizeMm: p.pageSizeMm,
          })),
          families: [...new Set(fillableDocPresets(m).map((p) => p.category))],
        };
      }
      let presets = fillablePresets(m);
      if (input.family) {
        presets = presets.filter((p) => p.category === input.family);
      }
      if (input.intent) {
        presets = presets.filter((p) => intentOf(p.id) === input.intent);
      }
      return {
        ok: true,
        type: "email",
        presets: presets.map((p) => ({
          presetId: p.id,
          family: p.category,
          intent: intentOf(p.id),
        })),
        families: [...new Set(fillablePresets(m).map((p) => p.category))],
        intents: PRESET_INTENTS,
      };
    };

    const getPresetSlots = async (rawInput: unknown) => {
      const m = await loadTemplateAiModules();
      const input = (rawInput ?? {}) as { presetId?: string; type?: string };
      // Email and document corpora share ticket ids (webinar-ticket lives in
      // BOTH) — type disambiguates; default email.
      if (input.type === "document") {
        const preset = fillableDocPresets(m).find((p) => p.id === input.presetId);
        if (!preset) {
          return {
            ok: false,
            errorCode: "unknownPresetId",
            validPresetIds: fillableDocPresets(m).map((p) => p.id),
          };
        }
        return {
          ok: true,
          presetId: preset.id,
          type: "document",
          slots: m.slots.walkPresetSlots(preset.craftJs),
          canvas: {
            contentAreaWidth: preset.contentAreaWidth,
            contentAreaHeight: preset.contentAreaHeight,
            pageSizeMm: preset.pageSizeMm,
            contentAreaBackgroundColor: preset.contentAreaBackgroundColor,
            linkColor: preset.linkColor,
            globalFont: preset.globalFont,
          },
        };
      }
      const preset = fillablePresets(m).find((p) => p.id === input.presetId);
      if (!preset) {
        return {
          ok: false,
          errorCode: "unknownPresetId",
          validPresetIds: fillablePresets(m).map((p) => p.id),
        };
      }
      return {
        ok: true,
        presetId: preset.id,
        type: "email",
        slots: m.slots.walkPresetSlots(preset.craftJs),
        canvas: {
          contentAreaWidth: preset.contentAreaWidth,
          contentAreaBackgroundColor: preset.contentAreaBackgroundColor,
          linkColor: preset.linkColor,
          globalFont: preset.globalFont,
        },
      };
    };

    const createTemplateFromPreset = async (rawInput: unknown) => {
      const m = await loadTemplateAiModules();
      const input = (rawInput ?? {}) as CreateInput;
      const preset = fillablePresets(m).find((p) => p.id === input.presetId);
      if (!preset) {
        return {
          ok: false,
          errorCode: "unknownPresetId",
          validPresetIds: fillablePresets(m).map((p) => p.id),
        };
      }
      const templateName = (input.templateName ?? "").trim();
      if (templateName.length < 3 || templateName.length > 100) {
        return { ok: false, errorCode: "invalidTemplateName", detail: "3-100 characters" };
      }
      if (!Array.isArray(input.languages) || input.languages.length === 0) {
        return { ok: false, errorCode: "languagesRequired" };
      }
      const langs = normalizeLocales(input.languages);
      if (!langs.ok) {
        return { ok: false, errorCode: "unknownLocale", detail: langs.badLocale };
      }
      const [primaryLanguage, ...additionalLanguages] = langs.locales;
      const subjects = normalizeSubjects(input.mailSubject ?? {});
      if (!subjects.ok) {
        return { ok: false, errorCode: "unknownLocale", detail: subjects.badLocale };
      }
      if ((subjects.map[primaryLanguage] ?? "").trim().length < 3) {
        return {
          ok: false,
          errorCode: "missingPrimarySubject",
          detail: `mailSubject["${primaryLanguage}"] needs at least 3 characters`,
        };
      }
      const overrides = input.colorOverrides ?? {};
      for (const color of [overrides.contentAreaBackgroundColor, overrides.linkColor]) {
        if (color !== undefined && !HEX_COLOR.test(color)) {
          return { ok: false, errorCode: "invalidColor", detail: String(color) };
        }
      }

      const filled = m.slots.applySlotFills(preset.craftJs, input.fills ?? {}, preset.id);
      if (!filled.ok) return filled;

      // Idempotency pre-check: the 10s client-tool timeout does NOT abort
      // the POST — a timed-out earlier attempt may already have created the
      // template. Same name present ⇒ report instead of duplicating.
      const existing = await getEmailTemplatesByEvent(
        eventIdRef.current,
        "emailTemplateId,templateName",
      );
      if (Array.isArray(existing)) {
        const duplicate = existing.find(
          (t) => (t.templateName ?? "").trim().toLowerCase() === templateName.toLowerCase(),
        );
        if (duplicate) {
          return {
            ok: false,
            errorCode: "templateNameExists",
            existingTemplateId: duplicate.emailTemplateId,
            detail: "a template with this name already exists — a previous timed-out call may have created it",
          };
        }
      }

      const payload = await m.core.buildCreateTemplatePayload({
        eventId: eventIdRef.current,
        templateName,
        senderAddressId: "",
        replyToMail: (input.replyToMail ?? "").trim(),
        bccMail: "",
        primaryLanguage,
        languages: additionalLanguages,
        mailSubject: subjects.map,
        selectedTemplateData: {
          craftJs: filled.craftJs,
          contentAreaWidth: preset.contentAreaWidth,
          contentAreaAlignment: preset.contentAreaAlignment,
          contentAreaBackgroundColor:
            overrides.contentAreaBackgroundColor ?? preset.contentAreaBackgroundColor,
          linkColor: overrides.linkColor ?? preset.linkColor,
          globalFont: preset.globalFont,
        },
        forceRenderAllLanguages: true,
      });
      // Art. 50 KI-VO: the server stamps the template as AI-created.
      payload.aiContentTool = "create_template_from_preset";
      const templateId = await m.core.postCreateTemplate(payload);
      return {
        ok: true,
        templateId,
        templateName,
        primaryLanguage,
        languages: langs.locales,
      };
    };

    const createDocumentFromPreset = async (rawInput: unknown) => {
      const m = await loadTemplateAiModules();
      const input = (rawInput ?? {}) as CreateDocumentInput;
      const preset = fillableDocPresets(m).find((p) => p.id === input.presetId);
      if (!preset) {
        return {
          ok: false,
          errorCode: "unknownPresetId",
          validPresetIds: fillableDocPresets(m).map((p) => p.id),
        };
      }
      const name = (input.name ?? "").trim();
      if (name.length < 3 || name.length > 100) {
        return { ok: false, errorCode: "invalidName", detail: "3-100 characters" };
      }
      if (!Array.isArray(input.languages) || input.languages.length === 0) {
        return { ok: false, errorCode: "languagesRequired" };
      }
      const langs = normalizeLocales(input.languages);
      if (!langs.ok) {
        return { ok: false, errorCode: "unknownLocale", detail: langs.badLocale };
      }
      const overrides = input.colorOverrides ?? {};
      for (const color of [overrides.contentAreaBackgroundColor, overrides.linkColor]) {
        if (color !== undefined && !HEX_COLOR.test(color)) {
          return { ok: false, errorCode: "invalidColor", detail: String(color) };
        }
      }

      const filled = m.slots.applySlotFills(preset.craftJs, input.fills ?? {}, preset.id);
      if (!filled.ok) return filled;

      // Same timeout idempotency story as the email create: pre-check for an
      // existing document with this name before POSTing again.
      const existing = await getDocumentTemplatesHandler(eventIdRef.current);
      if (Array.isArray(existing)) {
        const duplicate = existing.find(
          (t) => (t.name ?? "").trim().toLowerCase() === name.toLowerCase(),
        );
        if (duplicate) {
          return {
            ok: false,
            errorCode: "documentNameExists",
            existingTemplateId: duplicate.documentTemplateId,
            detail: "a document with this name already exists — a previous timed-out call may have created it",
          };
        }
      }

      const payload = await m.docCore.buildCreateDocumentPayload({
        eventId: eventIdRef.current,
        name,
        languages: langs.locales,
        selectedDocumentData: {
          craftJs: filled.craftJs,
          contentAreaWidth: preset.contentAreaWidth,
          contentAreaHeight: preset.contentAreaHeight,
          contentAreaAlignment: preset.contentAreaAlignment,
          contentAreaBackgroundColor:
            overrides.contentAreaBackgroundColor ?? preset.contentAreaBackgroundColor,
          linkColor: overrides.linkColor ?? preset.linkColor,
          globalFont: preset.globalFont,
        },
        forceRenderAllLanguages: true,
      });
      // Art. 50 KI-VO: the server stamps the document template's AI mark from
      // this; the mark then reaches the PDF metadata and the calendar file.
      payload.aiContentTool = "create_document_from_preset";
      const templateId = await m.docCore.postCreateDocument(payload, eventIdRef.current);
      return {
        ok: true,
        templateId,
        name,
        primaryLanguage: langs.locales[0],
        languages: langs.locales,
        pageSizeMm: preset.pageSizeMm,
      };
    };

    const updateAiTemplateSlots = async (rawInput: unknown) => {
      const m = await loadTemplateAiModules();
      const input = (rawInput ?? {}) as UpdateInput;
      if (!input.templateId) {
        return { ok: false, errorCode: "templateIdRequired" };
      }
      const hasFills = input.fills && Object.keys(input.fills).length > 0;
      const hasSubjects = input.mailSubject && Object.keys(input.mailSubject).length > 0;
      if (!hasFills && !hasSubjects) {
        return { ok: false, errorCode: "nothingToUpdate" };
      }

      const template = await getEmailTemplate(
        eventIdRef.current,
        input.templateId,
        [
          "emailTemplateId",
          "craftJs",
          "languages",
          "primaryLanguage",
          "contentAreaWidth",
          "contentAreaAlignment",
          "contentAreaBackgroundColor",
          "linkColor",
          "globalFont",
          "mailSubject",
          // Decides whether the re-rendered html may carry `@font-face`.
          "inlineImagesEnabled",
        ].join(","),
      );
      if (!template || "isServerError" in template) {
        return { ok: false, errorCode: "templateNotFound" };
      }
      const craftJs = template.craftJs ?? "";
      const presetId = m.slots.readAiPresetId(craftJs);
      if (!presetId) {
        return {
          ok: false,
          errorCode: "notAiCreatedTemplate",
          detail: "only templates created via create_template_from_preset can be slot-updated",
        };
      }

      // Structure guard: the user may have rebuilt the template in the
      // editor since creation. Compare the slot skeleton against the source
      // preset; any drift (or node types the walker doesn't know) means the
      // template is hand-edited — refuse rather than clobber their work.
      const preset = m.presets.EMAIL_TEMPLATE_PRESETS.find((p) => p.id === presetId);
      let currentSlots: ReturnType<typeof m.slots.walkPresetSlots>;
      try {
        currentSlots = m.slots.walkPresetSlots(craftJs);
      } catch {
        return { ok: false, errorCode: "templateEditedManually" };
      }
      if (preset) {
        const skeleton = (slots: ReturnType<typeof m.slots.walkPresetSlots>) =>
          slots.map((s) => `${s.slotId}:${s.kind}`).join("|");
        if (skeleton(currentSlots) !== skeleton(m.slots.walkPresetSlots(preset.craftJs))) {
          return { ok: false, errorCode: "templateEditedManually" };
        }
      }

      const filled = m.slots.applySlotFills(craftJs, input.fills ?? {}, presetId);
      if (!filled.ok) return filled;

      const canvas: TemplateCanvasSettings = {
        contentAreaAlignment: template.contentAreaAlignment,
        contentAreaWidth: template.contentAreaWidth,
        contentAreaBackgroundColor: template.contentAreaBackgroundColor,
        globalFont: template.globalFont,
        linkColor: template.linkColor,
        webfonts: emailWebfontsAllowed(template),
      };
      const allLangs = [
        template.primaryLanguage,
        ...(template.languages ?? []).filter((l) => l !== template.primaryLanguage),
      ];
      const html: Record<string, string> = {};
      for (const lang of allLangs) {
        const rendered = m.core.tryBuildLanguageHtml(lang, filled.craftJs, canvas, "email");
        // Never PUT "" over the template's real html (plan W0 §4).
        if (!rendered.ok) return { ok: false, errorCode: "templateHtmlRenderFailed", detail: lang };
        html[lang] = rendered.html;
      }

      const changedFields: Parameters<typeof updateEmailTemplate>[2] = {
        craftJs: filled.craftJs,
        html,
        // Art. 50 KI-VO: the server keeps/updates the template's AI mark.
        aiContentTool: "update_ai_template_slots",
      };
      if (hasSubjects) {
        const subjects = normalizeSubjects(input.mailSubject!);
        if (!subjects.ok) {
          return { ok: false, errorCode: "unknownLocale", detail: subjects.badLocale };
        }
        changedFields.mailSubject = { ...template.mailSubject, ...subjects.map };
      }
      const response = await updateEmailTemplate(
        eventIdRef.current,
        input.templateId,
        changedFields,
      );
      if (response && typeof response === "object" && "isServerError" in response) {
        throwServerError(response);
      }
      return {
        ok: true,
        templateId: input.templateId,
        updatedSlots: Object.keys(input.fills ?? {}).length,
        languagesRendered: allLangs,
      };
    };

    // Emit-tree lane: the model supplies a compact block DSL; the FE compiler
    // turns it into a GUARANTEED-valid, fully-editable Craft.js tree (no slots,
    // no preset) and saves it as a normal source="BUILDER" template. Mirrors
    // create_*_from_preset; the only difference is craftJs comes from the
    // compiler instead of a slot-filled preset.
    const generateEmailFromBlocks = async (rawInput: unknown) => {
      const m = await loadTemplateAiModules();
      const input = (rawInput ?? {}) as GenerateEmailInput;
      const templateName = (input.templateName ?? "").trim();
      if (templateName.length < 3 || templateName.length > 100) {
        return { ok: false, errorCode: "invalidTemplateName", detail: "3-100 characters" };
      }
      if (!Array.isArray(input.languages) || input.languages.length === 0) {
        return { ok: false, errorCode: "languagesRequired" };
      }
      const langs = normalizeLocales(input.languages);
      if (!langs.ok) {
        return { ok: false, errorCode: "unknownLocale", detail: langs.badLocale };
      }
      const [primaryLanguage, ...additionalLanguages] = langs.locales;
      const subjects = normalizeSubjects(input.subjects ?? {});
      if (!subjects.ok) {
        return { ok: false, errorCode: "unknownLocale", detail: subjects.badLocale };
      }
      if ((subjects.map[primaryLanguage] ?? "").trim().length < 3) {
        return {
          ok: false,
          errorCode: "missingPrimarySubject",
          detail: `subjects["${primaryLanguage}"] needs at least 3 characters`,
        };
      }
      const canvas = input.canvas ?? {};

      const compiled = m.compiler.buildCraftJsFromBlocks({
        languages: langs.locales,
        canvas,
        blocks: input.blocks ?? [],
      });
      if (!compiled.ok) return compiled;

      // Same timeout-idempotency story as create_template_from_preset.
      const existing = await getEmailTemplatesByEvent(
        eventIdRef.current,
        "emailTemplateId,templateName",
      );
      if (Array.isArray(existing)) {
        const duplicate = existing.find(
          (t) => (t.templateName ?? "").trim().toLowerCase() === templateName.toLowerCase(),
        );
        if (duplicate) {
          return {
            ok: false,
            errorCode: "templateNameExists",
            existingTemplateId: duplicate.emailTemplateId,
            detail: "a template with this name already exists — a previous timed-out call may have created it",
          };
        }
      }

      const payload = await m.core.buildCreateTemplatePayload({
        eventId: eventIdRef.current,
        templateName,
        senderAddressId: "",
        replyToMail: (input.replyToMail ?? "").trim(),
        bccMail: "",
        primaryLanguage,
        languages: additionalLanguages,
        mailSubject: subjects.map,
        selectedTemplateData: {
          craftJs: compiled.craftJs,
          contentAreaWidth: canvas.contentAreaWidth ?? 600,
          contentAreaAlignment: "center",
          // The NORMALIZED values, not the raw input: the compiler swaps `"`
          // for `'` in a font stack, and forwarding the raw string made the
          // renderer reject it and fall back to arial — permanently, since the
          // record persists what is forwarded here.
          contentAreaBackgroundColor:
            compiled.canvas.contentAreaBackgroundColor ?? "#ffffff",
          linkColor: compiled.canvas.linkColor ?? "#0000EE",
          globalFont: compiled.canvas.globalFont ?? "arial",
        },
        forceRenderAllLanguages: true,
      });
      // Art. 50 KI-VO: the server stamps the template as AI-created.
      payload.aiContentTool = "generate_email_from_blocks";
      const templateId = await m.core.postCreateTemplate(payload);
      return {
        ok: true,
        templateId,
        templateName,
        primaryLanguage,
        languages: langs.locales,
      };
    };

    const generateDocumentFromBlocks = async (rawInput: unknown) => {
      const m = await loadTemplateAiModules();
      const input = (rawInput ?? {}) as GenerateDocumentInput;
      const name = (input.name ?? "").trim();
      if (name.length < 3 || name.length > 100) {
        return { ok: false, errorCode: "invalidName", detail: "3-100 characters" };
      }
      if (!Array.isArray(input.languages) || input.languages.length === 0) {
        return { ok: false, errorCode: "languagesRequired" };
      }
      const langs = normalizeLocales(input.languages);
      if (!langs.ok) {
        return { ok: false, errorCode: "unknownLocale", detail: langs.badLocale };
      }
      const canvas = input.canvas ?? {};

      const compiled = m.compiler.buildCraftJsFromBlocks({
        languages: langs.locales,
        canvas,
        blocks: input.blocks ?? [],
      });
      if (!compiled.ok) return compiled;

      const existing = await getDocumentTemplatesHandler(eventIdRef.current);
      if (Array.isArray(existing)) {
        const duplicate = existing.find(
          (t) => (t.name ?? "").trim().toLowerCase() === name.toLowerCase(),
        );
        if (duplicate) {
          return {
            ok: false,
            errorCode: "documentNameExists",
            existingTemplateId: duplicate.documentTemplateId,
            detail: "a document with this name already exists — a previous timed-out call may have created it",
          };
        }
      }

      const payload = await m.docCore.buildCreateDocumentPayload({
        eventId: eventIdRef.current,
        name,
        languages: langs.locales,
        selectedDocumentData: {
          craftJs: compiled.craftJs,
          contentAreaWidth: canvas.contentAreaWidth ?? 600,
          contentAreaHeight: canvas.contentAreaHeight,
          contentAreaAlignment: "center",
          // The NORMALIZED values, not the raw input: the compiler swaps `"`
          // for `'` in a font stack, and forwarding the raw string made the
          // renderer reject it and fall back to arial — permanently, since the
          // record persists what is forwarded here.
          contentAreaBackgroundColor:
            compiled.canvas.contentAreaBackgroundColor ?? "#ffffff",
          linkColor: compiled.canvas.linkColor ?? "#0000EE",
          globalFont: compiled.canvas.globalFont ?? "arial",
        },
        forceRenderAllLanguages: true,
      });
      // Art. 50 KI-VO: see create_document_from_preset.
      payload.aiContentTool = "generate_document_from_blocks";
      const templateId = await m.docCore.postCreateDocument(payload, eventIdRef.current);
      return {
        ok: true,
        templateId,
        name,
        primaryLanguage: langs.locales[0],
        languages: langs.locales,
      };
    };

    register("list_template_presets", listTemplatePresets);
    register("get_preset_slots", getPresetSlots);
    register("create_template_from_preset", createTemplateFromPreset);
    register("create_document_from_preset", createDocumentFromPreset);
    register("update_ai_template_slots", updateAiTemplateSlots);
    register("generate_email_from_blocks", generateEmailFromBlocks);
    register("generate_document_from_blocks", generateDocumentFromBlocks);
    return () => {
      unregister("list_template_presets", listTemplatePresets);
      unregister("get_preset_slots", getPresetSlots);
      unregister("create_template_from_preset", createTemplateFromPreset);
      unregister("create_document_from_preset", createDocumentFromPreset);
      unregister("update_ai_template_slots", updateAiTemplateSlots);
      unregister("generate_email_from_blocks", generateEmailFromBlocks);
      unregister("generate_document_from_blocks", generateDocumentFromBlocks);
    };
  }, []);

  return null;
}
